import React, { useMemo, useState } from 'react';
import ConceptHeader from './ConceptHeader';
import ParameterCard from './ParameterCard';
import ModelDataDocumentationModal from '../ModelDataDocumentationModal';
import OutputTimeSeriesGrid from '../ModelDataFields/Output/TimeSeriesGrid';
import OutputTimeSeriesGridOperational from '../ModelDataFields/Output/TimeSeriesGridOperational';
import DynamicGridField from '../ModelDataFields/Output/DynamicGrid';
import FieldDependenciesPanel from '../ModelDataFields/Output/reusables/FieldDependenciesPanel';
import snakeToPascal from '../../tools';

const noop = () => {};

interface DetailPanelProps {
    selectedItem: any | null;
    tree: { items: Record<string, any>; rootId: string };
    currentState: any;
    reloadState: () => void;
    userRights: any;
    onSelectItem: (id: string) => void;
    scenarioId?: number | null;
}

export default function DetailPanel({
    selectedItem,
    tree,
    currentState,
    reloadState,
    userRights,
    onSelectItem,
    scenarioId = null,
}: DetailPanelProps) {
    const [docModalOpen, setDocModalOpen] = useState(false);
    const [depsGridFieldId, setDepsGridFieldId] = useState<number | null>(null);
    const [depsGridFieldName, setDepsGridFieldName] = useState<string>('');

    // Separate fields by type: parameters (float/grid), dynamic_grid, time_serie
    const parameterFields = useMemo(() => {
        if (!selectedItem) return [];
        return Object.entries(selectedItem.fields || {}).filter(
            ([_, field]: [string, any]) => field.type !== 'time_serie' && field.type !== 'dynamic_grid'
        );
    }, [selectedItem]);

    const dynamicGridFields = useMemo(() => {
        if (!selectedItem) return [];
        return Object.entries(selectedItem.fields || {}).filter(
            ([_, field]: [string, any]) => field.type === 'dynamic_grid'
        );
    }, [selectedItem]);

    const hasTimeSeries = useMemo(() => {
        if (!selectedItem) return false;
        return Object.values(selectedItem.fields || {}).some(
            (f: any) => f.type === 'time_serie'
        );
    }, [selectedItem]);

    // Empty state
    if (!selectedItem) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                    <span className="material-symbols-outlined text-slate-400 text-[32px]">
                        calculate
                    </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Select an item
                </h3>
                <p className="text-sm text-slate-500">
                    Click on an item in the tree to view its details and results.
                </p>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto">
            {/* A) ConceptHeader - sticky */}
            <ConceptHeader
                item={selectedItem}
                tree={tree}
                onBreadcrumbClick={onSelectItem}
                onDocumentationClick={() => setDocModalOpen(true)}
            />

            {/* B) Results */}
            {parameterFields.length > 0 && (
                <div className="px-6 py-4">
                    <ParameterCard
                        item={selectedItem}
                        fields={parameterFields}
                        reloadState={reloadState}
                        currentState={currentState}
                        userRights={userRights}
                        scenarioId={scenarioId}
                        onNavigate={onSelectItem}
                    />
                </div>
            )}

            {/* Dynamic Grids — full width */}
            {dynamicGridFields.map(([name, field]: [string, any]) => {
                field.model_data_id = selectedItem.id;
                return (
                    <div key={name} className="mb-6 border-t border-b border-slate-200">
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-l-2 border-l-primary border-b border-b-slate-200">
                            <span className="text-sm font-semibold text-slate-700">
                                {field.display_name || snakeToPascal(name)}
                            </span>
                            {field.compute_error && (
                                <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded inline-flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[14px]">warning</span>
                                    Error
                                </span>
                            )}
                            <button
                                onClick={() => { setDepsGridFieldId(field.id); setDepsGridFieldName(field.display_name || snakeToPascal(name)); }}
                                className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                                title="View dependencies"
                            >
                                <span className="material-symbols-outlined text-[16px]">device_hub</span>
                            </button>
                        </div>
                        <div className="overflow-auto relative z-0">
                            <DynamicGridField
                                name={snakeToPascal(name)}
                                field={field}
                                reloadState={reloadState}
                                currentState={currentState}
                                recreateExcelInput={noop}
                                compareWithField={null}
                                scrollRef={null}
                                onScroll={null}
                                scenarioId={scenarioId}
                                onNavigate={onSelectItem}
                            />
                        </div>
                    </div>
                );
            })}

            {/* Time series — full width */}
            {hasTimeSeries && (
                <div className="mb-6 flex-1 overflow-auto relative z-0 border-t border-b border-slate-200">
                    {currentState['Output']?.field_values ? (
                        <OutputTimeSeriesGrid
                            fields={selectedItem.fields}
                            currentState={currentState}
                        />
                    ) : (
                        <OutputTimeSeriesGridOperational
                            fields={selectedItem.fields}
                            containerClassName="overflow-auto relative z-0"
                            scenarioId={scenarioId}
                            onNavigate={onSelectItem}
                        />
                    )}
                </div>
            )}

            {/* Dependencies Modal for Dynamic Grids */}
            {scenarioId && (
                <FieldDependenciesPanel
                    isOpen={depsGridFieldId !== null}
                    onClose={() => setDepsGridFieldId(null)}
                    scenarioId={scenarioId}
                    fieldId={depsGridFieldId}
                    fieldName={depsGridFieldName}
                    onNavigate={onSelectItem}
                />
            )}

            {/* Documentation Modal */}
            <ModelDataDocumentationModal
                item={selectedItem}
                variant="Output"
                open={docModalOpen}
                setOpen={setDocModalOpen}
            />
        </div>
    );
}
